import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ToastService } from '@teorema/patron';
import { AccessType } from 'src/app/models/bw-user.model';
import { CompanyGroup } from 'src/app/models/company-group.model';
import { CoreConfig, System } from 'src/app/models/core-config.model';
import { PanelConfigurationService, utils } from 'src/app/shared';
import { OrderService } from 'src/app/shared/services/order.service';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { FirebaseErrors } from './reset-password/firebase-errors';

@Component({
  selector: 'patron-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AuthComponent implements OnInit, OnDestroy {
  authForm: FormGroup;
  user: any;
  firebaseToken!: string | null | undefined;
  companyGroups: any[] = [];
  access_types: { key: string; label: string; systems: string[] }[] = [];
  loading = false;
  config: CoreConfig;
  errorMessage: string = '';

  /**
   * Aviso de cadastro irregular (403 `CADASTRO_IRREGULAR`).
   *
   * Alerta fixo no topo do card, e não toast: é um recado que o cliente precisa
   * ler inteiro e repetir ao ligar para a Teorema — some antes disso não serve.
   */
  blockedMessage: string | null = null;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private toastService: ToastService,
    private titleService: Title,
    private panelConfigService: PanelConfigurationService,
    private orderService: OrderService
  ) {
    const user = utils.getUser();

    this.authForm = this.formBuilder.group({
      email: [user?.email || '', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      company_group: [null],
      user_reference: [''],
    });

    this.config = this.authService.getCoreConfig();
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.titleService.setTitle(`Entrar | ${this.config.panelTitle}`);
    }, 100);

    // Sessão derrubada pelo interceptor: o motivo veio junto na volta ao login.
    if (this.authService.blockedMessage) {
      this.blockedMessage = this.authService.blockedMessage;
      this.authService.blockedMessage = null;
    }
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(this.config.panelTitle!);
  }

  async apiGeralLogin(ignoreErrors = false) {
    return await this.authService
      .apiGeralLogin(
        this.authForm.get('email')?.value,
        this.authForm.get('password')?.value,
        this.firebaseToken,
        this.authForm.get('company_group')?.value?.id,
        this.authForm.get('user_reference')?.value?.id,
        this.config.firebaseProjectId
      )
      .catch(responseError => {
        if (ignoreErrors) return;

        // Cadastro irregular tem tela própria: o toast genérico "houve um erro"
        // mandaria o cliente procurar defeito no login em vez de ligar para a
        // Teorema. Sem licença (chamado 0000577675) usa a mesma tela: nenhum
        // grupo em que a pessoa tenha assento, com o controle de licenças ligado.
        const blockedCode = responseError?.error?.code;
        if (blockedCode == 'CADASTRO_IRREGULAR' || blockedCode == 'SEM_LICENCA') {
          this.blockedMessage = responseError?.error?.errors?.[0] || responseError?.error?.message;
          // O interceptor também guardou o recado; consumido aqui, ele não
          // reaparece numa visita futura à tela de login.
          this.authService.blockedMessage = null;
          return;
        }

        let message =
          responseError.status == 401
            ? 'Usuário e/ou senha incorreto(s)'
            : 'Houve um erro ao efetuar o login';
        if (!responseError?.messageShown) this.toastService.show({ color: 'error', message });
      });
  }

  async firebaseLogin() {
    let token: string | null | undefined = null;
    let response = await this.firebaseService
      .firebaseLogin(this.f['email'].value, this.f['password'].value)
      .catch(error => {
        if (error?.code) {
          this.errorMessage = FirebaseErrors.Parse(error.code);
        }
      });

    if (response) {
      let user = response.user;
      token = await user?.getIdToken();
    }

    return token;
  }

  async firebaseCreateUser() {
    return await this.firebaseService
      .firebaseCreateUser(this.f['email'].value, this.f['password'].value)
      .catch(error => {
        console.log(error);
      });
  }

  async onSubmit(ignoreErrors = false) {
    utils.clearStorageInfo();
    this.blockedMessage = null;
    this.toggleLoading();

    try {
      let firebaseUserExist = false;
      let hasPassword = false;

      if (!firebaseUserExist) {
        const userAuthMethods = await this.firebaseService.fetchSignInMethodsForEmail(
          this.f['email'].value
        );
        firebaseUserExist = userAuthMethods?.length > 0;
        hasPassword = userAuthMethods?.includes('password');
        if (firebaseUserExist && !this.firebaseToken && !this.isTempPassword()) {
          this.firebaseToken = await this.firebaseLogin();
        }
      }

      let apiGeralLoginResponse = await this.apiGeralLogin(ignoreErrors);

      if (apiGeralLoginResponse) {
        if (apiGeralLoginResponse.access_token) {
          let hasError = false;

          if (!firebaseUserExist) {
            this.firebaseCreateUser();
          } else if (!hasPassword && !this.firebaseToken) {
            await this.firebaseService.updatePassword(this.f['password'].value).catch(error => {
              hasError = true;

              this.toastService.show({ message: FirebaseErrors.Parse(error.code), color: 'error' });
            });
          }

          if (!hasError) {
            this.user = apiGeralLoginResponse.user;
            this.access_types = apiGeralLoginResponse.accesses_as;
            this.user.enable_support =
              this.access_types.find(at => at.key == AccessType.Support) != undefined;

            if (this.config.system != System.MULTIPANEL) {
              this.access_types = this.access_types.filter(
                (i: any) =>
                  i.systems.includes(this.config.system!.toString()) || i.systems.includes('*')
              );
            }
            utils.setToken(apiGeralLoginResponse.access_token);
            utils.setUser(this.user);
            utils.setLicenses(apiGeralLoginResponse.licenses);

            let userType = this.access_types.find(i => i.key == AccessType.User);
            let customerType = this.access_types.find(i => i.key == AccessType.Customer);
            let supplierType = this.access_types.find(i => i.key == AccessType.Supplier);

            if (supplierType && this.authService.checkSystem([System.QUOTES])) {
              const supplierBwCg = this.user.bw_user_company_groups?.find(
                (cg: any) => cg.supplier_id
              );

              if (supplierBwCg) {
                // Find matching CompanyGroup to ensure correct ID (group id) and pivot
                const cg = this.user.company_groups?.find(
                  (g: any) => g.id === supplierBwCg.company_group_id
                );

                // Fallback if not found: map BwUserCompanyGroup fields to mimic CompanyGroup structure
                // Crucial: id must be company_group_id
                const baseCg = cg || {
                  ...supplierBwCg,
                  id: supplierBwCg.company_group_id,
                  pivot: { supplier_id: supplierBwCg.supplier_id },
                };

                let supplierName = 'Fornecedor';
                // Check if supplier is an object (new backend) or array (old backend/cache)
                const supplier = Array.isArray(supplierBwCg?.supplier)
                  ? supplierBwCg?.supplier[0]
                  : supplierBwCg?.supplier;

                if (supplier?.person?.name) {
                  supplierName = supplier.person.name;
                }

                this.companyGroups.push({
                  ...baseCg,
                  access_type: supplierType,
                  name: supplierName,
                  displayName: supplierName,
                });
              }
            }

            this.user.company_groups
              .sort((a: any, b: any) => {
                if (a.name > b.name) return 1;
                if (a.name < b.name) return -1;
                return 0;
              })
              .forEach((cg: any) => {
                if (cg.pivot.user_id && userType) {
                  this.companyGroups.push({ ...cg, access_type: userType });
                }
                if (cg.pivot.customer_id && customerType) {
                  this.companyGroups.push({ ...cg, access_type: customerType });
                }
              });

            utils.setAvailableAccesses(this.companyGroups);

            if (this.companyGroups.length == 1 && !this.user.enable_support) {
              const cg = this.companyGroups[0];
              if (cg.id) utils.setCompanyGroup(cg);
              if (cg.access_type) utils.setAccessType(cg.access_type.key);
            }

            //Auto select cg Teorema login TicketCenter
            if (
              this.config.system == System.TICKET_CENTER &&
              this.companyGroups.length >= 1 &&
              this.user.enable_support
            ) {
              const cg = this.companyGroups?.find((companyGroup: CompanyGroup) => {
                return companyGroup.id == 'd354b40b-c56f-46a4-b033-33fcebdbac17';
              });
              if (cg.id) utils.setCompanyGroup(cg);
              if (cg.access_type) utils.setAccessType(cg.access_type.key);
            }

            this.authForm.get('company_group')?.setValidators(Validators.required);

            // Grupo guardado do acesso anterior que agora veio `blocked` (cadastro irregular ou,
            // chamado 0000577675, sem licença) não entra direto: a pessoa escolhe outro na lista,
            // onde o bloqueado aparece desabilitado com o motivo.
            const storedGroup: any = this.currentCompanyGroup;
            const storedGroupBlocked =
              !!storedGroup?.id &&
              (this.user.company_groups ?? []).some(
                (g: any) => g?.id === storedGroup.id && g?.blocked
              );

            if (
              (this.currentCompanyGroup && !storedGroupBlocked) ||
              (this.config.system == System.QUOTES && utils.isSupplier())
            ) {
              this.completeLogin();
            } else if (!this.companyGroups.length) {
              this.toastService.show({
                color: 'error',
                message: 'Usuário não relacionado a nenhuma empresa',
              });
            }
          }
        }
      }
    } catch (error: any) {
      if (ignoreErrors) return;
      this.toastService.show({ message: `Ocorreu um erro: ${error.message}`, color: 'error' });
    }

    this.toggleLoading();
  }

  get f() {
    return this.authForm.controls;
  }

  loadingGoogle: boolean = false;

  async googleLogin() {
    this.loadingGoogle = true;

    let response = await this.firebaseService.googleLogin().catch(error => {
      console.log(error);
    });

    if (response) {
      this.user = response.user;
      this.f['email'].setValue(this.user.email);
      this.f['password'].clearValidators();
      this.f['password'].updateValueAndValidity();
      this.firebaseToken = await this.user?.getIdToken();
      this.onSubmit();
    }

    this.loadingGoogle = false;
  }

  goToResetPassword() {
    const user = this.user || { ...this.authForm.value };
    if (user) {
      utils.setUser(user);
    }
    this.router.navigate(['recuperar-senha']);
  }

  async completeLogin() {
    if (!this.currentAccessType) return;
    if (this.router.url.includes(this.registerUrl)) return;
    let redirectPath = this.config.redirectPathAfterLogin;
    if (this.authService.checkSystem([System.QUOTES]) && utils.isSupplier()) {
      redirectPath = 'cotacoes-fornecedor';
    }
    this.panelConfigService.loadConfig();
    this.orderService.init();
    this.router.navigate([redirectPath]);
  }

  setAccessType(access_type: string) {
    utils.setAccessType(access_type);
  }

  getRefWithoutCompany(ref: string) {
    let result = ref;
    let split = ref?.split('_');
    if (split?.length > 1) {
      result = split[1];
    }
    return result;
  }

  toggleLoading() {
    this.loading = !this.loading;
  }

  logout() {
    this.authService.logout();
    window.location.reload();
  }

  isTempPassword() {
    const password = this.f['password'].value;
    return password.startsWith('TEMP') && password.length == 12;
  }

  get currentCompanyGroup() {
    return utils.getCompanyGroup();
  }

  get currentAccessType() {
    return utils.getAccessType();
  }

  get registerUrl() {
    return '/cadastrar-empresa';
  }

  @HostListener('window:keydown.Shift.n')
  goToRegister() {
    this.router.navigate([this.registerUrl]);
  }
}
